import { DemoBadge, MatchBadge, Notice } from "@/components/Badges";
import { FUNDING_TYPES, gbp, fmtDate, fmtDateTime, COST_CATEGORIES } from "@/lib/format";
import { ExternalLink, CheckCircle2, XCircle, HelpCircle, ShieldCheck, ShieldAlert, ChevronDown, Users } from "lucide-react";

const NP = "Not published";
const APPLICANTS = { private_sector: "Private sector businesses", individual: "Individuals / pre-trading", non_profit: "Non-profit organisations", public_sector: "Public sector organisations", research_organisation: "Eligible research organisations" };
const list = (arr, map) => (arr?.length ? arr.map((x) => map?.[x] || x).join(", ") : null);
const regions = (arr) => list(arr?.map((r) => (r === "National" ? "UK-wide" : r)));

const Row = ({ label, children, testid }) => (
  <div className="grid gap-1 border-b border-slate-100 py-3 sm:grid-cols-3" data-testid={testid}>
    <dt className="text-sm font-medium text-slate-500">{label}</dt>
    <dd className="text-sm text-slate-900 sm:col-span-2">{children || <span className="text-slate-400">{NP}. Check the funder's guidance.</span>}</dd>
  </div>
);

export const fundingRange = (o) => (o.award_min && o.award_max ? `${gbp(o.award_min)} – ${gbp(o.award_max)}` : o.award_max ? `Up to ${gbp(o.award_max)}` : o.award_min ? `From ${gbp(o.award_min)}` : null);

export const deadlineText = (o) => (o.rolling ? "Open, applications accepted continuously" : o.close_date ? `Closes ${fmtDate(o.close_date)}${o.deadline_note ? `, ${o.deadline_note}` : ""}` : o.status_note || "Deadline not published");

const contribution = (o) => {
  if (o.applicant_contribution) return o.applicant_contribution;
  if (o.funding_percentage && o.funding_percentage < 100) return `At least ${100 - o.funding_percentage}% of eligible project costs, as the funder covers up to ${o.funding_percentage}%.`;
  if (o.match_funding) return o.match_funding;
  return null;
};

export const VerificationBadge = ({ o }) => {
  const s = o.verification?.status || "unverified";
  const map = { verified: ["Verified against funder guidance", "bg-teal-50 text-teal-800", ShieldCheck], needs_review: ["Some figures under review", "bg-amber-50 text-amber-800", ShieldAlert], unverified: ["Not yet verified", "bg-slate-100 text-slate-600", ShieldAlert] };
  const [l, c, Icon] = map[s] || map.unverified;
  return <span data-testid="verification-badge" className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${c}`}><Icon className="h-3 w-3" aria-hidden />{l}</span>;
};

export const OppCardMeta = ({ o }) => (
  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
    <span><b className="text-slate-800">{FUNDING_TYPES[o.funding_type]}</b></span>
    <span>Funding per award: {fundingRange(o) || NP}</span>
    {o.funding_percentage ? <span>Rate: {o.funding_percentage}% of eligible costs</span> : null}
    {o.max_project_cost || o.project_cost_max ? <span>Max project cost: {gbp(o.max_project_cost || o.project_cost_max)} (not the grant)</span> : null}
    {o.in_kind_value ? <span>In-kind value: up to {gbp(o.in_kind_value)}</span> : null}
    {o.total_budget ? <span>Programme budget: {gbp(o.total_budget)}</span> : null}
    <span data-testid="opp-deadline">{deadlineText(o)}</span>
    <span className={o.status === "open" ? "text-teal-700" : o.status === "closed" ? "text-red-700" : "text-amber-700"}>{o.status}</span>
    <VerificationBadge o={o} />
  </div>
);

export const MatchPanel = ({ match }) => {
  const icon = { pass: <CheckCircle2 className="h-4 w-4 text-green-700" />, fail: <XCircle className="h-4 w-4 text-red-700" />, unknown: <HelpCircle className="h-4 w-4 text-amber-600" /> };
  return (
    <div className="card p-6" data-testid="match-panel">
      <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="font-semibold text-[#0B192C]">Eligibility against your profile</h2><MatchBadge status={match.status} /></div>
      <ul className="mt-4 space-y-2">
        {match.checks.map((c) => (
          <li key={c.criterion} className="flex gap-3 text-sm"><span className="mt-0.5">{icon[c.result]}</span><span><b>{c.criterion}:</b> {c.detail}</span></li>
        ))}
      </ul>
      <p className="mt-4 text-xs text-slate-500">{match.note}</p>
    </div>
  );
};

const WhoCanApply = ({ o }) => (o.who_can_apply || o.cannot_apply ? (
  <div className="mt-5 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" data-testid="who-can-apply">
    <Users className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
    <div><p className="font-semibold">Who can apply</p><p className="mt-1">{o.who_can_apply}</p>{o.cannot_apply && <p className="mt-1 font-medium">{o.cannot_apply}</p>}</div>
  </div>
) : null);

const Answer = ({ q, children, testid }) => (
  <div className="card p-5" data-testid={testid}>
    <h3 className="text-sm font-semibold text-[#0B192C]">{q}</h3>
    <div className="mt-2 space-y-1.5 text-sm leading-relaxed text-slate-700">{children}</div>
  </div>
);

const snippet = (t) => (t ? (t.length > 260 ? `${t.slice(0, 260).trim()}…` : t) : null);

const FiveAnswers = ({ o }) => (
  <section data-testid="five-answers" className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
    <Answer q="Can I apply?" testid="answer-can-apply">
      <p>{o.who_can_apply || list(o.eligible_applicants_raw?.length ? o.eligible_applicants_raw : o.eligible_applicants, APPLICANTS) || NP}</p>
      {o.cannot_apply && <p className="font-medium">{o.cannot_apply}</p>}
      <p className="text-xs text-slate-500">Where: {regions(o.regions) || "No location restriction published"}</p>
    </Answer>
    <Answer q="What will it fund?" testid="answer-what-funds">
      <p>{o.what_it_funds || snippet(o.summary_text || o.summary) || NP}</p>
      {o.eligible_expenditure?.length > 0 && <p className="text-xs text-slate-500">Eligible costs: {list(o.eligible_expenditure, COST_CATEGORIES)}</p>}
      {o.exclusions?.length > 0 && <p className="text-xs text-slate-500">Not funded: {list(o.exclusions)}</p>}
    </Answer>
    <Answer q="How much could I receive?" testid="answer-how-much">
      <p><b>{fundingRange(o) || NP}</b>{fundingRange(o) && " per award"}</p>
      {o.funding_percentage ? <p>Funding rate: {o.funding_percentage}% of eligible costs.</p> : null}
      {o.funding_basis && <p className="text-xs text-slate-500">{o.funding_basis}</p>}
    </Answer>
    <Answer q="What must I contribute?" testid="answer-contribute">
      <p>{contribution(o) || `${NP}. Check the funder's guidance.`}</p>
    </Answer>
    <Answer q="When and where do I apply?" testid="answer-when-where">
      <p>{o.rolling ? "Open, applications accepted continuously. There is no closing date." : o.close_date ? `${o.open_date ? `Opens ${fmtDate(o.open_date)}. ` : ""}Closes ${fmtDate(o.close_date)}${o.deadline_note ? `, ${o.deadline_note}` : ""}.` : o.status_note || "Deadline not published."}</p>
      {o.application_url && <a href={o.application_url} target="_blank" rel="noreferrer" data-testid="official-application-link" className="inline-flex items-center gap-1 font-semibold text-[#1F8A8B] underline">Apply on the official site <ExternalLink className="h-3.5 w-3.5" /></a>}
    </Answer>
  </section>
);

const isHeading = (line, next) => line.length <= 70 && !/[.,;]$/.test(line) && !!next && !/^[-•*]/.test(line);

export const toSections = (text) => {
  const lines = (text || "").split("\n").map((l) => l.trim()).filter(Boolean);
  const out = [];
  lines.forEach((l, i) => {
    if (isHeading(l, lines[i + 1]) && (i === 0 || lines[i - 1].length > 70 || /[.:]$/.test(lines[i - 1]))) out.push({ h: l.replace(/:$/, ""), body: [] });
    else (out.length ? out[out.length - 1] : out[out.push({ h: null, body: [] }) - 1]).body.push(l);
  });
  return out.filter((s) => s.body.length || s.h);
};

const ASSESS = /assess|shortlist|panel|interview|decision|feedback|review|score/i;

const Expand = ({ title, sections, testid }) => (
  <details className="group card p-0" data-testid={testid}>
    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-5 font-semibold text-[#0B192C]">{title}<ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" aria-hidden /></summary>
    <div className="space-y-4 border-t border-slate-100 px-5 pb-5 pt-4 text-sm leading-relaxed text-slate-700">
      {sections.map((s, i) => (
        <div key={i}>{s.h && <h4 className="font-semibold text-slate-900">{s.h}</h4>}{s.body.map((b, j) => <p key={j} className="mt-1">{b}</p>)}</div>
      ))}
    </div>
  </details>
);

const OfficialText = ({ o }) => {
  const dates = toSections(o.dates_text);
  const assess = dates.filter((s) => s.h && ASSESS.test(s.h));
  const keyDates = dates.filter((s) => !assess.includes(s));
  const blocks = [
    ["Summary", toSections(o.summary_text || o.summary), "official-summary"],
    ["Eligibility", toSections(o.eligibility_text), "official-eligibility-text"],
    ["Objectives and scope", toSections(o.objectives_text), "official-objectives"],
    ["Key dates", keyDates, "official-dates"],
    ["How applications are assessed", assess, "official-assessment"],
    ["How to apply", toSections(o.apply_text), "official-apply"],
  ].filter(([, s]) => s.length);
  if (!blocks.length) return null;
  return (
    <section className="space-y-3" data-testid="official-text">
      <h2 className="font-semibold text-[#0B192C]">Full official text</h2>
      {blocks.map(([t, s, id]) => <Expand key={id} title={t} sections={s} testid={id} />)}
    </section>
  );
};

const SourceStatus = ({ o }) => {
  const v = o.verification || {};
  return (
    <Notice tone={v.status === "verified" ? "info" : "warn"} testid="source-notice">
      <p><b>Source:</b> {o.source_name}{o.source_url && <> (<a className="underline" href={o.source_url} target="_blank" rel="noreferrer">view source</a>)</>}. <b>Source refreshed:</b> <span data-testid="source-refreshed">{fmtDateTime(o.last_checked)}</span>.</p>
      <p className="mt-1" data-testid="verification-status">
        <b>Information verified:</b>{" "}
        {v.status === "verified"
          ? <>yes, checked against {v.guidance_url ? <a className="underline" href={v.guidance_url} target="_blank" rel="noreferrer">{v.guidance_name || "the funder's guidance"}</a> : v.guidance_name || "the funder's guidance"} on {fmtDate(v.verified_at)}.</>
          : v.status === "needs_review" ? "not yet. Some source figures look like placeholders or have changed, so they are shown as 'Not published' until checked." : "not yet checked against the funder's own guidance."}
      </p>
      {v.flags?.length > 0 && <ul className="mt-1 list-disc pl-5 text-xs">{v.flags.map((f, i) => <li key={i}>{f.issue}</li>)}</ul>}
      <p className="mt-1">A successful refresh does not mean the details are correct. Always confirm on the funder's guidance before applying.</p>
    </Notice>
  );
};

export default function OpportunityView({ o, actions }) {
  return (
    <div className="space-y-6">
      <div>
        <div className="flex flex-wrap gap-2">{o.is_demo && <DemoBadge text="Demonstration record - not a live opportunity" />}<span className="caption">{o.funder}</span></div>
        <h1 data-testid="opportunity-title" className="mt-2 text-2xl font-bold text-[#0B192C] sm:text-3xl">{o.title}</h1>
        <OppCardMeta o={o} />
        <WhoCanApply o={o} />
        {actions && <div className="mt-5 flex flex-wrap gap-2">{actions}</div>}
      </div>
      <FiveAnswers o={o} />
      <div className="card px-6 py-2" data-testid="funding-details">
        <h2 className="pt-3 font-semibold text-[#0B192C]">Funding details</h2>
        <dl>
          <Row label="Maximum eligible project cost" testid="fin-project-cost">{o.max_project_cost || o.project_cost_max ? `${o.project_cost_min ? `${gbp(o.project_cost_min)} – ` : "Up to "}${gbp(o.max_project_cost || o.project_cost_max)} total project cost. This is not the amount you receive.` : null}</Row>
          <Row label="Funding percentage" testid="fin-percentage">{o.funding_percentage ? `${o.funding_percentage}% of eligible costs${o.funding_basis ? `. ${o.funding_basis}` : ""}` : null}</Row>
          <Row label="Maximum funding contribution" testid="fin-max-funding">{fundingRange(o) ? `${fundingRange(o)} per award` : null}</Row>
          <Row label="Applicant contribution" testid="fin-contribution">{contribution(o)}</Row>
          <Row label="Total programme budget" testid="fin-programme-budget">{o.total_budget ? `${gbp(o.total_budget)} across all awards (not an individual limit)` : null}</Row>
          {o.in_kind_value && <Row label="In-kind support value">{`Up to ${gbp(o.in_kind_value)} of services or support, not cash`}</Row>}
          <Row label="Funder / programme">{o.funder}{o.programme && o.programme !== o.title ? ` — ${o.programme}` : ""}</Row>
          <Row label="Funding type">{FUNDING_TYPES[o.funding_type]}{o.funding_type !== "grant" && " (not a grant)"}</Row>
          <Row label="Opening date / deadline">{o.rolling ? "No closing date. Applications are accepted continuously." : o.close_date ? `${fmtDate(o.open_date)} – ${fmtDate(o.close_date)}${o.deadline_note ? ` (${o.deadline_note})` : ""} · ${o.status}` : o.status_note || null}</Row>
          {o.status_note && <Row label="Current intake">{o.status_note}</Row>}
          <Row label="Regions">{regions(o.regions)}</Row>
          <Row label="Sectors">{list(o.sectors) || (o.structured_criteria ? "No sector restriction published" : null)}</Row>
          <Row label="Required partners">{o.required_partners}</Row>
          <Row label="Required evidence">{list(o.required_evidence)}</Row>
        </dl>
      </div>
      {o.match_notes && <section className="card p-6" data-testid="match-notes"><h2 className="font-semibold text-[#0B192C]">Who this is for and what to check</h2><p className="mt-3 text-sm leading-relaxed text-slate-700">{o.match_notes}</p></section>}
      <OfficialText o={o} />
      <SourceStatus o={o} />
    </div>
  );
}
